import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "../../shared";
import type { CardOffer } from "../../../services/cards/mocks";
// Button всё ещё используется в success-view (IssueSuccess).
import { getUserInfo } from "../../../services/me";
import {
  IssueCardResponse,
  issueCard,
} from "../../../services/cards";

interface IssueCardDrawerProps {
  open: boolean;
  offer: CardOffer | null;
  onClose: () => void;
}

interface FormState {
  cardholderName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const EMPTY_FORM: FormState = {
  cardholderName: "",
  dateOfBirth: "",
  email: "",
  phone: "",
  addressLine1: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
};

type IssueFlow = "airwallex" | "highnote" | "generic";

/**
 * По коду провайдера решаем, какой набор полей показывать юзеру.
 *
 *   • airwallex — **INDIVIDUAL personalized**: имя, DOB, email, телефон и
 *     полный адрес держателя (country/city/line1/postcode). Всё это идёт в
 *     Airwallex-cardholder ДО создания карты (см. `AirwallexService`).
 *
 *   • highnote  — US consumer: полный KYC (имя, DOB, email, телефон, адрес).
 *
 *   • generic   — фолбэк для новых провайдеров: только имя, дальше сервер
 *     сам решит, что требовать.
 */
function pickFlow(offer: CardOffer | null): IssueFlow {
  if (!offer) return "generic";
  const code = offer.providerCode?.toLowerCase();
  if (code === "airwallex") return "airwallex";
  if (code === "highnote") return "highnote";
  // Legacy-фолбэк для старых mocks без providerCode: по имени провайдера.
  const src = `${offer.provider} ${offer.description}`.toLowerCase();
  if (src.includes("highnote")) return "highnote";
  if (src.includes("airwallex")) return "airwallex";
  return "generic";
}

/** Полная KYC-форма нужна и для Airwallex (INDIVIDUAL), и для Highnote. */
function needsFullKyc(flow: IssueFlow): boolean {
  return flow === "airwallex" || flow === "highnote";
}

/**
 * Дефолт страны в форме зависит от провайдера: у Airwallex мы работаем через
 * гонконгский юрлицо (BOO), поэтому стартовая страна = HK. Highnote — US.
 */
function defaultCountryFor(flow: IssueFlow): string {
  if (flow === "airwallex") return "HK";
  return "US";
}

export function IssueCardDrawer({ open, offer, onClose }: IssueCardDrawerProps) {
  const theme = useTheme();
  const qc = useQueryClient();
  const { t } = useTranslation("cards");
  const { data: user } = useQuery({
    queryFn: getUserInfo,
    queryKey: ["me"],
    enabled: open,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [issued, setIssued] = useState<IssueCardResponse | null>(null);
  const [revealSecrets, setRevealSecrets] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const flow = useMemo(() => pickFlow(offer), [offer]);

  // Автозаполнение имени/телефона из initData + смена дефолтной страны при
  // смене оффера (Airwallex → HK, Highnote → US).
  useEffect(() => {
    if (!open) return;
    setIssued(null);
    setRevealSecrets(false);
    setError(null);
    const defaultCountry = defaultCountryFor(flow);
    setForm((prev) => ({
      ...prev,
      cardholderName:
        prev.cardholderName ||
        [user?.first_name, user?.last_name].filter(Boolean).join(" ").toUpperCase() ||
        "",
      phone: prev.phone || user?.phone_number || "",
      // Если юзер ещё ничего не выбирал в стране — подставляем дефолт от
      // провайдера. Если уже трогал (не совпадает с EMPTY_FORM.country) —
      // не перезаписываем, чтобы не сбить ввод.
      country:
        prev.country && prev.country !== EMPTY_FORM.country ? prev.country : defaultCountry,
    }));
  }, [open, user, flow]);

  const validate = (): string | null => {
    if (!offer) return t("issue.errors.no_offer");
    if (!form.cardholderName.trim()) return t("issue.errors.name_required");
    if (needsFullKyc(flow)) {
      // INDIVIDUAL (Airwallex) и US consumer (Highnote) требуют одинаковый
      // KYC-набор: DOB + email + phone + полный адрес.
      if (!form.dateOfBirth) return t("issue.errors.dob_required");
      const email = form.email.trim();
      if (!email) return t("issue.errors.email_required");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        return t("issue.errors.email_invalid");
      // Phone для Airwallex формально опционален, но мы всё равно просим —
      // Airwallex использует его для 3DS/OTP на транзакциях.
      if (flow === "highnote" && !form.phone.trim())
        return t("issue.errors.phone_required");
      if (!form.addressLine1.trim()) return t("issue.errors.address_required");
      if (!form.city.trim()) return t("issue.errors.city_required");
      if (!form.postalCode.trim()) return t("issue.errors.zip_required");
    }
    return null;
  };

  const { mutate: submit, isLoading } = useMutation(
    () => {
      const fullKyc = needsFullKyc(flow);
      return issueCard({
        offerId: offer!.id,
        // Для personalized-карт (Airwallex INDIVIDUAL) имя из формы попадает
        // и в cardholder, и в PAN. Для generic-провайдеров сервер решит сам.
        cardholderName: form.cardholderName.trim(),
        dateOfBirth: fullKyc ? form.dateOfBirth || undefined : undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: fullKyc
          ? {
              line1: form.addressLine1.trim(),
              city: form.city.trim(),
              state: form.state.trim() || undefined,
              postalCode: form.postalCode.trim(),
              country: form.country.trim().toUpperCase(),
            }
          : undefined,
      });
    },
    {
      onSuccess: (data) => {
        setError(null);
        setIssued(data);
        qc.invalidateQueries(["my-cards"]);
      },
      onError: (err) => {
        const data = (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data;
        const raw = data?.message;
        const msg = Array.isArray(raw)
          ? raw.join("; ")
          : raw ||
            (err as { message?: string })?.message ||
            t("issue.errors.generic");
        setError(msg);
      },
    }
  );

  const handleSubmit = () => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    submit();
  };

  const handleClose = () => {
    if (isLoading) return;
    setForm(EMPTY_FORM);
    setIssued(null);
    setRevealSecrets(false);
    onClose();
  };

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar(t("issue.copied", { label }), { variant: "success" });
    } catch {
      /* ignore */
    }
  };

  // Единый стиль полей (filled-look поверх background.default),
  // чтобы поля хорошо читались на тёмном/светлом фоне drawer'а.
  // `theme.palette.divider` в тёмной теме = rgba(255,255,255,0.07) — на
  // paper-фоне drawer'а он практически не виден. Здесь берём чуть контрастнее
  // (0.16 / 0.14) — по мотивам iOS "elevated input".
  const borderColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.16)"
      : "rgba(0, 0, 0, 0.14)";

  // Важно: в глобальной теме (`baseThemeParameters.ts`) есть override
  //   MuiTextField.styleOverrides.root: { "& fieldset": { border: "none" } }
  // Он приходит с большим весом CSS и просто убирает рамку у всех TextField.
  // Переопределяем именно `border` shorthand-ом (border-style + width + color) —
  // иначе `borderColor` не помогает, потому что `border-style: none` остаётся.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.default,
      "& fieldset": { border: `1px solid ${borderColor}` },
      "&:hover fieldset": { border: `1px solid ${theme.palette.primary.main}` },
      "&.Mui-focused fieldset": {
        border: `1.5px solid ${theme.palette.primary.main}`,
      },
    },
    "& .MuiInputBase-input": { padding: "12px 14px" },
    "& .MuiInputLabel-root": { fontSize: 14 },
  } as const;

  // Для `<Select>` тот же вид, что у TextField. Плюс MenuProps: без zIndex меню
  // рендерится в Portal с дефолтным z-index (1300) — а Drawer у нас 100010,
  // поэтому dropdown уходит под backdrop drawer'а и «мерцает».
  const selectSx = {
    borderRadius: "12px",
    backgroundColor: theme.palette.background.default,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor,
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1.5px",
    },
  } as const;

  const selectMenuProps = {
    // Портал меню должен быть выше drawer'а (zIndex 100010), иначе dropdown
    // уходит под backdrop и его не видно / он слипается со страницей.
    sx: { zIndex: 100020 },
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.secondary.main,
        backgroundImage: "none",
        borderRadius: "14px",
        marginTop: "6px",
        border: `1px solid ${borderColor}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 12px 32px rgba(0,0,0,0.45)"
            : "0 12px 32px rgba(16,24,40,0.12)",
        "& .MuiMenuItem-root": {
          fontSize: 14,
          minHeight: 40,
        },
      },
    },
    // Уменьшаем «дёрганье» — меню появляется под инпутом, а не по центру.
    anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
    transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
  } as const;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: 100010 }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" },
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 28px",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          width: "44px",
          height: "4px",
          borderRadius: "4px",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          margin: "0 auto 14px",
          flexShrink: 0,
        }}
      />

      {issued ? (
        <IssueSuccess
          issued={issued}
          flow={flow}
          reveal={revealSecrets}
          onToggleReveal={() => setRevealSecrets((v) => !v)}
          onCopy={copy}
          onClose={handleClose}
          fieldBg={theme.palette.background.default}
          t={t}
        />
      ) : (
        <>
          <Box sx={{ flexShrink: 0, mb: 1.5 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
              {t("issue.title")}
            </Typography>
            {offer && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {offer.provider} · {offer.name} · {offer.price}
              </Typography>
            )}
            {flow === "airwallex" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, lineHeight: 1.45 }}
              >
                {t("issue.airwallex_hint")}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              display: "grid",
              gap: 1.25,
              pr: 0.5,
              // Пространство под sticky-кнопкой снизу.
              pb: 1,
            }}
          >
            <TextField
              label={t("issue.fields.name")}
              size="small"
              value={form.cardholderName}
              onChange={(e) =>
                setForm((f) => ({ ...f, cardholderName: e.target.value.toUpperCase() }))
              }
              placeholder={t("issue.fields.name_placeholder")}
              fullWidth
              sx={fieldSx}
            />

            {needsFullKyc(flow) && (
              <>
                <TextField
                  label={t("issue.fields.dob")}
                  type="date"
                  size="small"
                  value={form.dateOfBirth}
                  onChange={(e) => setForm((f) => ({ ...f, dateOfBirth: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label={t("issue.fields.email")}
                  size="small"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="user@example.com"
                  fullWidth
                  sx={fieldSx}
                  inputMode="email"
                  autoComplete="email"
                />
                <TextField
                  label={
                    flow === "airwallex"
                      ? t("issue.fields.phone_optional")
                      : t("issue.fields.phone")
                  }
                  size="small"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+15555550100"
                  fullWidth
                  sx={fieldSx}
                  inputMode="tel"
                  autoComplete="tel"
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    display: "block",
                  }}
                >
                  {t("issue.fields.holder_address")}
                </Typography>

                <TextField
                  label={t("issue.fields.address_line")}
                  size="small"
                  value={form.addressLine1}
                  onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))}
                  placeholder={
                    flow === "airwallex" ? "1 Test Street" : "1 Market St"
                  }
                  fullWidth
                  sx={fieldSx}
                />
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  <TextField
                    label={t("issue.fields.city")}
                    size="small"
                    value={form.city}
                    onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                    placeholder={
                      flow === "airwallex" ? "Hong Kong" : "San Francisco"
                    }
                    fullWidth
                    sx={fieldSx}
                  />
                  <TextField
                    label={t("issue.fields.state")}
                    size="small"
                    value={form.state}
                    onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                    placeholder={flow === "airwallex" ? t("issue.state_optional") : "CA"}
                    helperText={t("issue.state_hint")}
                    fullWidth
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  <TextField
                    label={t("issue.fields.zip")}
                    size="small"
                    value={form.postalCode}
                    onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
                    placeholder={flow === "airwallex" ? "000000" : "94105"}
                    fullWidth
                    sx={fieldSx}
                  />
                  <Select
                    size="small"
                    value={form.country}
                    onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                    fullWidth
                    sx={selectSx}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="HK">Hong Kong</MenuItem>
                    <MenuItem value="SG">Singapore</MenuItem>
                    <MenuItem value="US">United States</MenuItem>
                    <MenuItem value="GB">United Kingdom</MenuItem>
                    <MenuItem value="AE">UAE</MenuItem>
                    <MenuItem value="DE">Germany</MenuItem>
                    <MenuItem value="RU">Russia</MenuItem>
                  </Select>
                </Box>

                {flow === "airwallex" && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      lineHeight: 1.45,
                      mt: 0.5,
                    }}
                  >
                    {t("issue.airwallex_consent")}
                  </Typography>
                )}
              </>
            )}
          </Box>

          <Box sx={{ flexShrink: 0, pt: 1.5 }}>
            {error && (
              <Box
                sx={{
                  mb: 1.25,
                  px: 1.5,
                  py: 1,
                  borderRadius: "12px",
                  bgcolor: "rgba(239, 49, 36, 0.12)",
                  border: "1px solid rgba(239, 49, 36, 0.35)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#EF3124",
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                  }}
                >
                  {error}
                </Typography>
              </Box>
            )}
            <ButtonBase
              component="button"
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={{
                width: "100%",
                minHeight: 48,
                borderRadius: "40px",
                bgcolor: "primary.main",
                color: "#fff",
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: 15,
                letterSpacing: "-0.5px",
                boxShadow: "0 5px 30px 0 rgba(0,0,0,0.25)",
                transition: "opacity 0.15s",
                "&:active": { opacity: 0.85 },
                "&.Mui-disabled": { opacity: 0.6 },
              }}
            >
              {isLoading ? t("issue.submitting") : t("apply.cta")}
            </ButtonBase>
          </Box>
        </>
      )}
    </Drawer>
  );
}

// ─────────── Success view ───────────

/**
 * После submit есть два кейса:
 *
 *   • `status === "PROCESSING"` — карта в review (Airwallex обычно возвращает
 *     сразу `PENDING`; ACTIVE прилетит webhook'ом за 1–2 минуты). Показываем
 *     нейтральный экран «В обработке» без PAN/CVV.
 *
 *   • `status === "ACTIVE"` + PAN/CVV — Highnote test-env / mock. Классический
 *     экран с реквизитами и кнопкой reveal.
 *
 *   • `status === "ACTIVE"` без PAN/CVV — Airwallex после автоактивации.
 *     Показываем только last4 + ссылку «открыть подробнее» → в детальный экран.
 */
function IssueSuccess({
  issued,
  flow,
  reveal,
  onToggleReveal,
  onCopy,
  onClose,
  fieldBg,
  t,
}: {
  issued: IssueCardResponse;
  flow: IssueFlow;
  reveal: boolean;
  onToggleReveal: () => void;
  onCopy: (value: string, label: string) => void;
  onClose: () => void;
  fieldBg: string;
  t: TFunction;
}) {
  const isProcessing = (issued.status || "").toUpperCase() === "PROCESSING";
  const hasSecrets = Boolean(issued.pan || issued.cvv);

  if (isProcessing) {
    return (
      <Box
        sx={{
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box sx={{ textAlign: "center", py: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255, 176, 32, 0.16)",
              color: "#FFB020",
              mb: 1.5,
            }}
          >
            <Icon icon="solar:clock-circle-bold-duotone" width={32} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
            {t("issue.pending_title")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.75, lineHeight: 1.5, px: 1 }}
          >
            {flow === "airwallex"
              ? t("issue.pending_hint_airwallex")
              : t("issue.pending_hint_default")}
          </Typography>
        </Box>

        <Box
          sx={{
            borderRadius: "16px",
            p: 2,
            bgcolor: fieldBg,
            display: "grid",
            gap: 1,
          }}
        >
          <SecretRow label={t("issue.card_name")} value={issued.name} onCopy={onCopy} />
          <SecretRow
            label={t("detail.exp")}
            value={issued.expiry || "—"}
            monospace
            onCopy={onCopy}
          />
        </Box>

        <Button onClick={onClose}>{t("issue.done")}</Button>
      </Box>
    );
  }

  const panDisplay = issued.pan
    ? reveal
      ? issued.pan
      : `•••• •••• •••• ${issued.last_four}`
    : `•••• •••• •••• ${issued.last_four}`;
  const cvvDisplay = issued.cvv ? (reveal ? issued.cvv : "•••") : "—";

  return (
    <Box
      sx={{
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box sx={{ textAlign: "center", py: 1 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,202,72,0.14)",
            color: "#00CA48",
            mb: 1.5,
          }}
        >
          <Icon icon="solar:check-circle-bold-duotone" width={32} />
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
          {t("issue.success_title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {issued.name} · {issued.currency}
        </Typography>
      </Box>

      <Box
        sx={{
          borderRadius: "16px",
          p: 2,
          bgcolor: fieldBg,
          display: "grid",
          gap: 1.5,
        }}
      >
        <SecretRow
          label={t("issue.card_number")}
          value={panDisplay}
          monospace
          copyValue={issued.pan}
          onCopy={onCopy}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          <SecretRow
            label={t("detail.exp")}
            value={issued.expiry || "—"}
            monospace
            copyValue={issued.expiry}
            onCopy={onCopy}
          />
          <SecretRow
            label={t("detail.cvv")}
            value={cvvDisplay}
            monospace
            copyValue={issued.cvv}
            onCopy={onCopy}
          />
        </Box>
      </Box>

      {hasSecrets && (
        <Button onClick={onToggleReveal} disableGlassEffect>
          {reveal ? t("issue.hide_details") : t("issue.reveal_details")}
        </Button>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textAlign: "center", lineHeight: 1.4 }}
      >
        {hasSecrets ? t("issue.pan_hint") : t("issue.pan_hint_secure")}
      </Typography>

      <Button onClick={onClose}>{t("issue.done")}</Button>
    </Box>
  );
}

function SecretRow({
  label,
  value,
  monospace,
  copyValue,
  onCopy,
}: {
  label: string;
  value: string;
  monospace?: boolean;
  copyValue?: string;
  onCopy: (value: string, label: string) => void;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block" }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
        <Typography
          sx={{
            fontFamily: monospace ? "monospace" : undefined,
            fontSize: 15,
            fontWeight: 600,
            flex: 1,
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>
        {copyValue && (
          <IconButton
            size="small"
            onClick={() => onCopy(copyValue, label)}
            sx={{ color: "text.secondary" }}
          >
            <Icon icon="solar:copy-linear" width={16} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
